import React, { useState } from 'react';
import { Box, Typography, Card, CardContent, Button, useTheme, Grid } from '@mui/material';
import { StarOutlined, TrendingUpOutlined, PsychologyOutlined } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReviewsCompareModal from 'sections/premiumBlock/ReviewsCompareModal';

import { openSnackbar } from 'store/reducers/snackbar';

const ReviewsAnalysisBlock = ({ url }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const userPlan = useSelector((state) => state.proAccountReducer.plan);
  const [searchParams, setSearchParams] = useSearchParams();
  const isReviewsModalOpen = searchParams.get('showReviewsModal') === 'true';
  const [isReviewsLoading, setIsReviewsLoading] = useState(false);

  // Function to detect if URL is for a mobile app
  const isMobileAppUrl = (url) => {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    return lowerUrl.includes('play.google.com') || lowerUrl.includes('apps.apple.com') || lowerUrl.includes('appstore.com');
  };

  // Function to format URL for reviews analysis
  const formatUrl = (url) => {
    if (!url) return '';

    // Remove protocol prefixes
    if (url.startsWith('http://')) {
      url = url.slice(7);
    } else if (url.startsWith('https://')) {
      url = url.slice(8);
    } else if (url.startsWith('www.')) {
      url = url.slice(4);
    }

    return url;
  };

  // Function to start reviews analysis
  const startReviewsAnalysis = async (url, isApp) => {
    setIsReviewsLoading(true);
    try {
      // Set the URL in Redux state for the reviews page (use a new action type for clarity)
      dispatch({
        type: 'SET_REVIEWS_URL',
        payload: { reviewsUrl: url, isApp }
      });
      // Navigate to reviews page - it will handle the analysis
      navigate('/reviews');
    } catch (error) {
      console.error('Error navigating to reviews page:', error);
      dispatch(
        openSnackbar({
          open: true,
          message: 'Failed to navigate to reviews page. Please try again.',
          variant: 'alert',
          alert: { color: 'error' },
          close: false
        })
      );
    } finally {
      setIsReviewsLoading(false);
    }
  };

  const handleReviewsAnalysis = () => {
    // Track the event with Yandex Metrika
    if (window.ym) {
      if (userPlan === 'Free') {
        window.ym(94496474, 'reachGoal', 'REVIEWS_ANALYSIS_FREE');
      } else {
        window.ym(94496474, 'reachGoal', 'REVIEWS_ANALYSIS');
      }
    }

    // Show upsell modal ONLY for users on the 'Free' plan.
    // Users on 'Trial' or any paid plan get direct access.
    if (userPlan === 'Free') {
      console.log('ReviewsAnalysisBlock - opening reviews modal (Free plan)');
      setIsReviewsLoading(true);
      setSearchParams({ showReviewsModal: 'true' });
      return;
    }

    // For paid users, start actual reviews analysis
    const isApp = isMobileAppUrl(url);
    const formattedUrl = formatUrl(url);
    startReviewsAnalysis(formattedUrl, isApp);
  };

  const handleCloseReviewsModal = () => {
    console.log('ReviewsAnalysisBlock - closing reviews modal');
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('showReviewsModal');
    setSearchParams(newSearchParams, { replace: true });
    setIsReviewsLoading(false);
  };

  const features = [
    {
      icon: <StarOutlined sx={{ fontSize: 28, color: theme.palette.primary.main }} />,
      title: 'Sentiment Analysis',
      description: 'Understand customer emotions and satisfaction levels'
    },
    {
      icon: <TrendingUpOutlined sx={{ fontSize: 28, color: theme.palette.success.main }} />,
      title: 'Trend Identification',
      description: 'Spot patterns and trends in customer feedback'
    },
    {
      icon: <PsychologyOutlined sx={{ fontSize: 28, color: theme.palette.warning.main }} />,
      title: 'Feature Requests',
      description: 'Discover what customers want most from your product'
    }
  ];

  return (
    <>
      <Box sx={{ width: '100%', mt: 6, mb: 4 }}>
        <Card
          sx={{
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(52, 211, 153, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #3b82f6 0%, #34d399 100%)'
            }
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h3" sx={{ mb: 2, fontWeight: 'bold', color: 'text.primary' }}>
                Analyze Customer Reviews
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3, maxWidth: '600px', mx: 'auto' }}>
                Analyze customer reviews for your product and discover actionable insights to improve your product and stay ahead of the
                competition.
              </Typography>
            </Box>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 'medium' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      {feature.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleReviewsAnalysis}
                disabled={isReviewsLoading}
                startIcon={isReviewsLoading ? null : <StarOutlined />}
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 600
                }}
              >
                {isReviewsLoading ? 'Loading...' : 'Analyze Reviews'}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Get comprehensive insights from customer feedback in minutes
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <ReviewsCompareModal open={isReviewsModalOpen} handleClose={handleCloseReviewsModal} />
    </>
  );
};

export default ReviewsAnalysisBlock;
